import { signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { updateState } from '@angular-architects/ngrx-toolkit';
import { ChatMessageModel } from '@klant/conversational-connector/models/ChatMessage.model';
import { ConversationModel } from '@klant/conversational-connector/models/Conversation.model';
import { PendingMessage, Platform } from '@klant/conversational-connector/klant-conversational-connector.model';

import { computed, inject } from '@angular/core';

import { ApError } from '@ap/error';

import { ChatSessionStore } from '../chat-sesion-store/chat-session.store';
import { withDebugDevTools } from '../../../debug/devtools.features';

type ConversationalChatState = {
    chatMessages: ChatMessageModel[];
    conversation: ConversationModel | null;
    pendingMessages: PendingMessage[];
    unreadChatMessages: ChatMessageModel[];
    takeoverError: ApError | undefined;
    connectionError: string | undefined;
    idleTimerActive: boolean;
    showLiveChatFaqPopUp: boolean;
    platform: Platform;
};

const initialState: ConversationalChatState = {
    chatMessages: [],
    conversation: null,
    pendingMessages: [],
    unreadChatMessages: [],
    takeoverError: undefined,
    connectionError: undefined,
    idleTimerActive: false,
    showLiveChatFaqPopUp: false,
    platform: undefined
};

const RETRY_CONNECTION_ERROR = 'retry connection error';
const CONNECTION_ERROR = 'connection error';

export type ConversationalChatStore = InstanceType<typeof ConversationalChatStore>;

export const ConversationalChatStore = signalStore(
    { providedIn: 'root' },
    withState(initialState),
    withDebugDevTools('conversational-chat'),
    withComputed((store, chatSessionStore = inject(ChatSessionStore)) => ({
        showRetryConnectionError: computed(() => !store.takeoverError() && store.connectionError() === RETRY_CONNECTION_ERROR),
        showConnectionError: computed(() => !store.takeoverError() && store.connectionError() === CONNECTION_ERROR),
        showChat: computed(() => !!chatSessionStore.chatSession() && !store.takeoverError() && !store.connectionError())
    })),
    withMethods((store) => ({
        setConversation(conversation: ConversationModel): void {
            updateState(store, 'Conversation was initialized', { conversation });
        },

        setChatMessages(messages: ChatMessageModel[]): void {
            updateState(store, 'Chat messages were loaded', { chatMessages: messages });
        },
        addChatMessage(message: ChatMessageModel): void {
            updateState(store, 'New chat message was added', {
                chatMessages: [...store.chatMessages(), message]
            });
        },
        updateChatMessage(chatMessage: ChatMessageModel): void {
            const messages = store.chatMessages();
            const index = messages.findIndex((m) => m.transactionId === chatMessage.transactionId);
            if (index !== -1) {
                updateState(store, 'Chat message was updated', {
                    chatMessages: messages.map((msg, i) => (i === index ? chatMessage : msg))
                });
            }
        },

        addPendingMessage(message: PendingMessage): void {
            updateState(store, 'Pending message was added', {
                pendingMessages: [...store.pendingMessages(), message]
            });
        },
        removePendingMessage(transactionId: string): void {
            updateState(store, 'Pending message was removed', {
                pendingMessages: store.pendingMessages().filter((m) => m.transactionId !== transactionId)
            });
        },

        addUnreadMessages(messages: ChatMessageModel[]): void {
            updateState(store, 'Unread messages were added', {
                unreadChatMessages: [...store.unreadChatMessages(), ...messages]
            });
        },
        clearUnreadMessages(): void {
            updateState(store, 'Unread messages were cleared', { unreadChatMessages: [] });
        },

        setTakeoverError(error: ApError | undefined): void {
            updateState(store, 'Takeover error was set', { takeoverError: error, connectionError: undefined });
        },
        setConnectionError(error: string | undefined): void {
            updateState(store, 'Connection error was set', { connectionError: error });
        },

        setIdleTimerActive(idleTimerActive: boolean): void {
            updateState(store, 'Idle timer was activated', { idleTimerActive });
        },
        setShowLiveChatFaqPopUp(show: boolean): void {
            updateState(store, 'Live chat FAQ popup visibility was updated', { showLiveChatFaqPopUp: show });
        },

        setPlatform(platform: Platform): void {
            updateState(store, 'Platform was set', { platform });
        }
    }))
);
